import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";

// Mock images for gallery
const mockImages = {
  person: [
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400",
    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400",
  ],
  organization: [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400",
    "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400",
    "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=400",
    "https://images.unsplash.com/photo-1554469384-e58fac16e23a?w=400",
  ],
  location: [
    "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400",
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400",
    "https://images.unsplash.com/photo-1514565131-fce0801e5785?w=400",
    "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=400",
  ],
  event: [
    "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400",
    "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=400",
    "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400",
    "https://images.unsplash.com/photo-1511578314322-379afb476865?w=400",
  ],
  document: [
    "https://images.unsplash.com/photo-1568667256549-094345857637?w=400",
    "https://images.unsplash.com/photo-1456324504439-367cee3b3c32?w=400",
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400",
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400",
  ],
};

// Extended mock data with more attributes
const mockDatabase = {
  person: [
    { id: 1, name: "John Smith", firstName: "John", lastName: "Smith", role: "CEO", company: "TechCorp Inc.", email: "john.smith@techcorp.com", phone: "+1 555-0101", mobile: "+1 555-0111", location: "New York", status: "active", department: "Executive", dateOfBirth: "1975-03-15", nationality: "American", gender: "Male", linkedIn: "linkedin.com/johnsmith", education: "MBA Harvard", yearsExperience: 25, salary: "$450,000", clearanceLevel: "Top Secret", lastLogin: "2024-04-01", notes: "Key stakeholder", relevance: 98 },
    { id: 2, name: "Sarah Johnson", firstName: "Sarah", lastName: "Johnson", role: "CTO", company: "TechCorp Inc.", email: "sarah.j@techcorp.com", phone: "+1 555-0102", mobile: "+1 555-0112", location: "San Francisco", status: "active", department: "Technology", dateOfBirth: "1982-07-22", nationality: "American", gender: "Female", linkedIn: "linkedin.com/sarahj", education: "PhD MIT", yearsExperience: 18, salary: "$380,000", clearanceLevel: "Secret", lastLogin: "2024-04-02", notes: "AI Expert", relevance: 95 },
    { id: 3, name: "Michael Chen", firstName: "Michael", lastName: "Chen", role: "CFO", company: "Capital Ventures", email: "m.chen@capven.com", phone: "+1 555-0103", mobile: "+1 555-0113", location: "Boston", status: "active", department: "Finance", dateOfBirth: "1978-11-08", nationality: "Chinese-American", gender: "Male", linkedIn: "linkedin.com/mchen", education: "MBA Wharton", yearsExperience: 22, salary: "$420,000", clearanceLevel: "Confidential", lastLogin: "2024-03-28", notes: "Financial expert", relevance: 87 },
    { id: 4, name: "Emily Davis", firstName: "Emily", lastName: "Davis", role: "Director", company: "DataFlow Ltd.", email: "emily.d@dataflow.io", phone: "+1 555-0104", mobile: "+1 555-0114", location: "Chicago", status: "inactive", department: "Operations", dateOfBirth: "1985-02-14", nationality: "British", gender: "Female", linkedIn: "linkedin.com/emilyd", education: "MS Stanford", yearsExperience: 15, salary: "$280,000", clearanceLevel: "Confidential", lastLogin: "2024-01-15", notes: "On sabbatical", relevance: 82 },
    { id: 5, name: "Robert Wilson", firstName: "Robert", lastName: "Wilson", role: "Manager", company: "TechCorp Inc.", email: "r.wilson@techcorp.com", phone: "+1 555-0105", mobile: "+1 555-0115", location: "New York", status: "active", department: "Sales", dateOfBirth: "1988-09-30", nationality: "American", gender: "Male", linkedIn: "linkedin.com/rwilson", education: "BA NYU", yearsExperience: 12, salary: "$185,000", clearanceLevel: "Unclassified", lastLogin: "2024-04-01", notes: "Top performer", relevance: 75 },
    { id: 6, name: "Lisa Anderson", firstName: "Lisa", lastName: "Anderson", role: "Analyst", company: "Capital Ventures", email: "l.anderson@capven.com", phone: "+1 555-0106", mobile: "+1 555-0116", location: "Boston", status: "active", department: "Research", dateOfBirth: "1992-05-18", nationality: "Canadian", gender: "Female", linkedIn: "linkedin.com/landerson", education: "MS Columbia", yearsExperience: 8, salary: "$125,000", clearanceLevel: "Secret", lastLogin: "2024-04-02", notes: "Rising star", relevance: 71 },
  ],
  organization: [
    { id: 1, name: "TechCorp Inc.", legalName: "TechCorp Incorporated", industry: "Technology", sector: "Software", employees: "500+", revenue: "$50M", founded: "2015", headquarters: "New York", status: "public", stockSymbol: "TECH", marketCap: "$2.5B", ceo: "John Smith", website: "techcorp.com", phone: "+1 555-1000", taxId: "12-3456789", incorporationDate: "2015-01-15", country: "USA", region: "North America", type: "Corporation", rating: "AAA", lastAudit: "2024-01-15", relevance: 96 },
    { id: 2, name: "Capital Ventures", legalName: "Capital Ventures LLC", industry: "Finance", sector: "Investment", employees: "200+", revenue: "$120M", founded: "2008", headquarters: "Boston", status: "private", stockSymbol: "-", marketCap: "-", ceo: "Michael Chen", website: "capven.com", phone: "+1 555-2000", taxId: "23-4567890", incorporationDate: "2008-06-20", country: "USA", region: "North America", type: "LLC", rating: "AA", lastAudit: "2024-02-01", relevance: 91 },
    { id: 3, name: "DataFlow Ltd.", legalName: "DataFlow Limited", industry: "Data Analytics", sector: "Technology", employees: "150+", revenue: "$25M", founded: "2018", headquarters: "San Francisco", status: "private", stockSymbol: "-", marketCap: "-", ceo: "James Brown", website: "dataflow.io", phone: "+1 555-3000", taxId: "34-5678901", incorporationDate: "2018-03-10", country: "USA", region: "North America", type: "Ltd", rating: "A", lastAudit: "2024-01-30", relevance: 85 },
    { id: 4, name: "Global Logistics", legalName: "Global Logistics Corp", industry: "Transportation", sector: "Logistics", employees: "1000+", revenue: "$200M", founded: "1995", headquarters: "Chicago", status: "public", stockSymbol: "GLBL", marketCap: "$1.8B", ceo: "Patricia Moore", website: "globallog.com", phone: "+1 555-4000", taxId: "45-6789012", incorporationDate: "1995-09-05", country: "USA", region: "North America", type: "Corporation", rating: "AA", lastAudit: "2024-02-15", relevance: 78 },
    { id: 5, name: "EuroTech GmbH", legalName: "EuroTech GmbH", industry: "Technology", sector: "Hardware", employees: "300+", revenue: "€45M", founded: "2010", headquarters: "Berlin", status: "private", stockSymbol: "-", marketCap: "-", ceo: "Hans Mueller", website: "eurotech.de", phone: "+49 30 1234", taxId: "DE123456789", incorporationDate: "2010-04-22", country: "Germany", region: "Europe", type: "GmbH", rating: "A", lastAudit: "2024-01-20", relevance: 72 },
  ],
  location: [
    { id: 1, name: "New York Office", type: "Office", address: "123 Wall Street", city: "New York", state: "NY", postalCode: "10005", country: "USA", region: "North America", capacity: "200", status: "operational", sqFootage: "25000", floors: "3", parkingSpaces: "50", securityLevel: "High", yearBuilt: "2005", lastRenovation: "2022", monthlyRent: "$85,000", utilities: "Included", accessibility: "Full", relevance: 94 },
    { id: 2, name: "San Francisco HQ", type: "Headquarters", address: "456 Market St", city: "San Francisco", state: "CA", postalCode: "94105", country: "USA", region: "North America", capacity: "350", status: "operational", sqFootage: "45000", floors: "5", parkingSpaces: "100", securityLevel: "Maximum", yearBuilt: "2015", lastRenovation: "2023", monthlyRent: "$150,000", utilities: "Separate", accessibility: "Full", relevance: 89 },
    { id: 3, name: "London Branch", type: "Branch", address: "10 Downing St", city: "London", state: "-", postalCode: "SW1A 2AA", country: "UK", region: "Europe", capacity: "100", status: "operational", sqFootage: "12000", floors: "2", parkingSpaces: "20", securityLevel: "High", yearBuilt: "1890", lastRenovation: "2020", monthlyRent: "£45,000", utilities: "Included", accessibility: "Partial", relevance: 82 },
    { id: 4, name: "Berlin Office", type: "Office", address: "Unter den Linden 5", city: "Berlin", state: "-", postalCode: "10117", country: "Germany", region: "Europe", capacity: "75", status: "planned", sqFootage: "8000", floors: "2", parkingSpaces: "15", securityLevel: "Standard", yearBuilt: "2018", lastRenovation: "-", monthlyRent: "€25,000", utilities: "Separate", accessibility: "Full", relevance: 76 },
    { id: 5, name: "Tokyo Office", type: "Office", address: "1-1 Marunouchi", city: "Tokyo", state: "-", postalCode: "100-0005", country: "Japan", region: "Asia Pacific", capacity: "120", status: "operational", sqFootage: "15000", floors: "3", parkingSpaces: "30", securityLevel: "High", yearBuilt: "2012", lastRenovation: "2021", monthlyRent: "¥2,500,000", utilities: "Included", accessibility: "Full", relevance: 70 },
  ],
  event: [
    { id: 1, name: "Annual Summit 2024", type: "Conference", date: "2024-06-15", endDate: "2024-06-17", location: "New York", venue: "Javits Center", attendees: "500", status: "upcoming", organizer: "TechCorp Inc.", budget: "$250,000", ticketPrice: "$500", speakers: "25", sponsors: "15", category: "Technology", format: "Hybrid", registrationDeadline: "2024-06-01", catering: "Full Service", parking: "Available", relevance: 97 },
    { id: 2, name: "Q1 Board Meeting", type: "Meeting", date: "2024-03-28", endDate: "2024-03-28", location: "Boston", venue: "Capital Ventures HQ", attendees: "12", status: "completed", organizer: "Capital Ventures", budget: "$5,000", ticketPrice: "-", speakers: "4", sponsors: "-", category: "Corporate", format: "In-Person", registrationDeadline: "-", catering: "Light Refreshments", parking: "Reserved", relevance: 88 },
    { id: 3, name: "Product Launch", type: "Launch", date: "2024-05-10", endDate: "2024-05-10", location: "San Francisco", venue: "Moscone Center", attendees: "250", status: "upcoming", organizer: "DataFlow Ltd.", budget: "$150,000", ticketPrice: "$200", speakers: "8", sponsors: "10", category: "Product", format: "Hybrid", registrationDeadline: "2024-05-01", catering: "Reception", parking: "Available", relevance: 84 },
    { id: 4, name: "Training Workshop", type: "Workshop", date: "2024-04-20", endDate: "2024-04-22", location: "Chicago", venue: "Training Center", attendees: "30", status: "upcoming", organizer: "Global Logistics", budget: "$15,000", ticketPrice: "$100", speakers: "3", sponsors: "-", category: "Training", format: "In-Person", registrationDeadline: "2024-04-15", catering: "Lunch Provided", parking: "Free", relevance: 72 },
    { id: 5, name: "Industry Webinar", type: "Webinar", date: "2024-04-05", endDate: "2024-04-05", location: "Virtual", venue: "Online", attendees: "1000", status: "completed", organizer: "TechCorp Inc.", budget: "$8,000", ticketPrice: "Free", speakers: "5", sponsors: "3", category: "Education", format: "Virtual", registrationDeadline: "2024-04-04", catering: "-", parking: "-", relevance: 68 },
  ],
  document: [
    { id: 1, name: "Q4 Financial Report", type: "Report", author: "Michael Chen", department: "Finance", pages: "45", created: "2024-01-15", modified: "2024-01-20", status: "approved", version: "2.1", fileSize: "2.5 MB", format: "PDF", classification: "Confidential", reviewedBy: "Board", approvedBy: "CEO", expiryDate: "2025-01-15", tags: "financial, quarterly", accessLevel: "Executive", downloads: "156", language: "English", relevance: 95 },
    { id: 2, name: "Project Alpha Proposal", type: "Proposal", author: "Sarah Johnson", department: "Engineering", pages: "28", created: "2024-02-20", modified: "2024-03-01", status: "pending", version: "1.3", fileSize: "1.8 MB", format: "PDF", classification: "Internal", reviewedBy: "CTO", approvedBy: "-", expiryDate: "-", tags: "project, proposal", accessLevel: "Manager", downloads: "45", language: "English", relevance: 90 },
    { id: 3, name: "Employee Handbook 2024", type: "Manual", author: "HR Team", department: "Human Resources", pages: "120", created: "2024-01-01", modified: "2024-02-15", status: "approved", version: "5.0", fileSize: "5.2 MB", format: "PDF", classification: "Internal", reviewedBy: "Legal", approvedBy: "HR Director", expiryDate: "2025-01-01", tags: "hr, policy, handbook", accessLevel: "All Employees", downloads: "892", language: "English", relevance: 85 },
    { id: 4, name: "Market Analysis", type: "Analysis", author: "Emily Davis", department: "Marketing", pages: "35", created: "2024-03-05", modified: "2024-03-10", status: "draft", version: "0.9", fileSize: "3.1 MB", format: "PDF", classification: "Confidential", reviewedBy: "-", approvedBy: "-", expiryDate: "-", tags: "market, analysis, research", accessLevel: "Marketing Team", downloads: "12", language: "English", relevance: 79 },
    { id: 5, name: "Technical Specification", type: "Specification", author: "Dev Team", department: "Engineering", pages: "85", created: "2024-02-01", modified: "2024-03-20", status: "approved", version: "3.2", fileSize: "4.5 MB", format: "PDF", classification: "Internal", reviewedBy: "Tech Lead", approvedBy: "CTO", expiryDate: "-", tags: "technical, specs, api", accessLevel: "Engineering", downloads: "234", language: "English", relevance: 74 },
  ],
};

// Extended entity configurations with mandatory fields
const entityConfig = {
  person: {
    icon: "fa-user",
    color: "hsl(var(--chart-1))",
    fields: [
      { key: "name", label: "Full Name", type: "text", placeholder: "Enter full name...", mandatory: true },
      { key: "firstName", label: "First Name", type: "text", placeholder: "First name..." },
      { key: "lastName", label: "Last Name", type: "text", placeholder: "Last name...", mandatory: true },
      { key: "email", label: "Email", type: "text", placeholder: "Email address...", mandatory: true },
      { key: "phone", label: "Phone", type: "text", placeholder: "Phone number..." },
      { key: "mobile", label: "Mobile", type: "text", placeholder: "Mobile number..." },
      { key: "role", label: "Role", type: "select", options: ["CEO", "CTO", "CFO", "Director", "Manager", "Engineer", "Analyst", "Consultant"] },
      { key: "company", label: "Company", type: "text", placeholder: "Company name..." },
      { key: "department", label: "Department", type: "select", options: ["Executive", "Technology", "Finance", "Operations", "Sales", "Marketing", "Research", "HR", "Legal"] },
      { key: "location", label: "Location", type: "select", options: ["New York", "San Francisco", "Boston", "Chicago", "London", "Berlin", "Tokyo", "Singapore"] },
      { key: "status", label: "Status", type: "select", options: ["active", "inactive", "pending", "terminated"] },
      { key: "nationality", label: "Nationality", type: "text", placeholder: "Nationality..." },
      { key: "gender", label: "Gender", type: "select", options: ["Male", "Female", "Other", "Prefer not to say"] },
      { key: "education", label: "Education", type: "text", placeholder: "Highest education..." },
      { key: "yearsExperience", label: "Years Experience", type: "text", placeholder: "Years..." },
      { key: "clearanceLevel", label: "Clearance Level", type: "select", options: ["Unclassified", "Confidential", "Secret", "Top Secret"] },
      { key: "linkedIn", label: "LinkedIn", type: "text", placeholder: "LinkedIn profile..." },
    ],
    columns: ["name", "role", "company", "email", "department", "location", "status"],
    facets: ["status", "department", "location", "role", "clearanceLevel"],
  },
  organization: {
    icon: "fa-building",
    color: "hsl(var(--chart-2))",
    fields: [
      { key: "name", label: "Name", type: "text", placeholder: "Organization name...", mandatory: true },
      { key: "legalName", label: "Legal Name", type: "text", placeholder: "Legal entity name..." },
      { key: "industry", label: "Industry", type: "select", options: ["Technology", "Finance", "Data Analytics", "Transportation", "Healthcare", "Manufacturing", "Retail", "Energy"], mandatory: true },
      { key: "sector", label: "Sector", type: "text", placeholder: "Sector..." },
      { key: "employees", label: "Employees", type: "select", options: ["1-50", "50-100", "100-200", "200+", "500+", "1000+", "5000+"] },
      { key: "revenue", label: "Revenue", type: "text", placeholder: "Annual revenue..." },
      { key: "founded", label: "Founded", type: "text", placeholder: "Year founded..." },
      { key: "headquarters", label: "Headquarters", type: "text", placeholder: "City..." },
      { key: "country", label: "Country", type: "select", options: ["USA", "UK", "Germany", "France", "Japan", "China", "Canada", "Australia"] },
      { key: "region", label: "Region", type: "select", options: ["North America", "Europe", "Asia Pacific", "Latin America", "Middle East", "Africa"] },
      { key: "status", label: "Status", type: "select", options: ["public", "private", "government", "non-profit"], mandatory: true },
      { key: "type", label: "Entity Type", type: "select", options: ["Corporation", "LLC", "Ltd", "GmbH", "Partnership", "Sole Proprietor"] },
      { key: "stockSymbol", label: "Stock Symbol", type: "text", placeholder: "Ticker symbol..." },
      { key: "ceo", label: "CEO", type: "text", placeholder: "CEO name..." },
      { key: "website", label: "Website", type: "text", placeholder: "Website URL..." },
      { key: "rating", label: "Rating", type: "select", options: ["AAA", "AA", "A", "BBB", "BB", "B", "CCC"] },
    ],
    columns: ["name", "industry", "employees", "revenue", "headquarters", "country", "status"],
    facets: ["status", "industry", "country", "region", "type", "rating"],
  },
  location: {
    icon: "fa-location-dot",
    color: "hsl(var(--chart-5))",
    fields: [
      { key: "name", label: "Name", type: "text", placeholder: "Location name...", mandatory: true },
      { key: "type", label: "Type", type: "select", options: ["Office", "Headquarters", "Branch", "Warehouse", "Factory", "Data Center", "Retail"], mandatory: true },
      { key: "address", label: "Address", type: "text", placeholder: "Street address..." },
      { key: "city", label: "City", type: "text", placeholder: "City name...", mandatory: true },
      { key: "state", label: "State/Province", type: "text", placeholder: "State..." },
      { key: "postalCode", label: "Postal Code", type: "text", placeholder: "ZIP/Postal code..." },
      { key: "country", label: "Country", type: "select", options: ["USA", "UK", "Germany", "France", "Japan", "China", "Canada", "Australia"] },
      { key: "region", label: "Region", type: "select", options: ["North America", "Europe", "Asia Pacific", "Latin America", "Middle East", "Africa"] },
      { key: "capacity", label: "Capacity", type: "text", placeholder: "Number of people..." },
      { key: "sqFootage", label: "Square Footage", type: "text", placeholder: "Sq ft..." },
      { key: "floors", label: "Floors", type: "text", placeholder: "Number of floors..." },
      { key: "status", label: "Status", type: "select", options: ["operational", "planned", "under construction", "closed", "for sale"] },
      { key: "securityLevel", label: "Security Level", type: "select", options: ["Standard", "High", "Maximum", "Restricted"] },
      { key: "accessibility", label: "Accessibility", type: "select", options: ["Full", "Partial", "Limited", "None"] },
    ],
    columns: ["name", "type", "address", "city", "country", "capacity", "status"],
    facets: ["status", "type", "country", "region", "securityLevel"],
  },
  event: {
    icon: "fa-calendar",
    color: "hsl(var(--chart-4))",
    fields: [
      { key: "name", label: "Event Name", type: "text", placeholder: "Event name...", mandatory: true },
      { key: "type", label: "Type", type: "select", options: ["Conference", "Meeting", "Launch", "Workshop", "Webinar", "Training", "Seminar", "Trade Show"], mandatory: true },
      { key: "date", label: "Start Date", type: "date", mandatory: true },
      { key: "endDate", label: "End Date", type: "date" },
      { key: "location", label: "Location", type: "text", placeholder: "Location..." },
      { key: "venue", label: "Venue", type: "text", placeholder: "Venue name..." },
      { key: "attendees", label: "Attendees", type: "text", placeholder: "Expected attendees..." },
      { key: "status", label: "Status", type: "select", options: ["upcoming", "ongoing", "completed", "cancelled", "postponed"] },
      { key: "organizer", label: "Organizer", type: "text", placeholder: "Organizing company..." },
      { key: "budget", label: "Budget", type: "text", placeholder: "Budget amount..." },
      { key: "ticketPrice", label: "Ticket Price", type: "text", placeholder: "Ticket price..." },
      { key: "category", label: "Category", type: "select", options: ["Technology", "Corporate", "Product", "Training", "Education", "Networking", "Industry"] },
      { key: "format", label: "Format", type: "select", options: ["In-Person", "Virtual", "Hybrid"] },
      { key: "speakers", label: "Speakers", type: "text", placeholder: "Number of speakers..." },
      { key: "sponsors", label: "Sponsors", type: "text", placeholder: "Number of sponsors..." },
    ],
    columns: ["name", "type", "date", "location", "attendees", "organizer", "status"],
    facets: ["status", "type", "category", "format"],
  },
  document: {
    icon: "fa-file-lines",
    color: "hsl(var(--chart-3))",
    fields: [
      { key: "name", label: "Document Name", type: "text", placeholder: "Document name...", mandatory: true },
      { key: "type", label: "Type", type: "select", options: ["Report", "Proposal", "Manual", "Analysis", "Contract", "Memo", "Specification", "Policy"], mandatory: true },
      { key: "author", label: "Author", type: "text", placeholder: "Author name...", mandatory: true },
      { key: "department", label: "Department", type: "select", options: ["Finance", "Engineering", "Human Resources", "Marketing", "Operations", "Legal", "Sales", "Executive"] },
      { key: "status", label: "Status", type: "select", options: ["draft", "pending", "approved", "archived", "expired", "under review"] },
      { key: "classification", label: "Classification", type: "select", options: ["Public", "Internal", "Confidential", "Restricted", "Top Secret"] },
      { key: "format", label: "Format", type: "select", options: ["PDF", "Word", "Excel", "PowerPoint", "Text", "HTML"] },
      { key: "version", label: "Version", type: "text", placeholder: "Version number..." },
      { key: "created", label: "Created Date", type: "date" },
      { key: "modified", label: "Modified Date", type: "date" },
      { key: "pages", label: "Pages", type: "text", placeholder: "Number of pages..." },
      { key: "fileSize", label: "File Size", type: "text", placeholder: "File size..." },
      { key: "accessLevel", label: "Access Level", type: "select", options: ["All Employees", "Manager", "Executive", "Engineering", "Marketing Team", "HR Only"] },
      { key: "tags", label: "Tags", type: "text", placeholder: "Tags (comma separated)..." },
      { key: "language", label: "Language", type: "select", options: ["English", "German", "French", "Spanish", "Japanese", "Chinese"] },
    ],
    columns: ["name", "type", "author", "department", "status", "classification", "version"],
    facets: ["status", "type", "department", "classification", "format", "accessLevel"],
  },
};

// Search Form Component with customizable fields
const SearchForm = ({ entityType, onSearch, config, onSaveToWidget }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({});
  // Default: show ALL fields
  const [activeFields, setActiveFields] = useState(
    config.fields.map(f => f.key)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // Note: Mandatory fields are marked visually but not required for search
    // Users can search with partial criteria
    onSearch(entityType, formData);
  };

  const handleClear = () => {
    setFormData({});
  };

  const toggleField = (key) => {
    const field = config.fields.find(f => f.key === key);
    if (field?.mandatory) return; // Can't remove mandatory fields
    
    setActiveFields(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const selectAllFields = () => {
    setActiveFields(config.fields.map(f => f.key));
  };

  const deselectAllFields = () => {
    // Keep only mandatory fields
    setActiveFields(config.fields.filter(f => f.mandatory).map(f => f.key));
  };

  const visibleFields = config.fields.filter(f => activeFields.includes(f.key));

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <p className="text-xs text-muted-foreground">
          <span className="text-red-500 font-bold">*</span> {t("mandatoryFields") || "Mandatory fields"}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs sm:text-sm" onClick={handleClear}>
            <i className="fa-solid fa-rotate-left mr-1 sm:mr-2"></i>
            <span className="hidden sm:inline">{t("clearForm") || "Clear Form"}</span>
            <span className="sm:hidden">{t("clear") || "Clear"}</span>
          </Button>
          {onSaveToWidget && (
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs sm:text-sm" onClick={() => onSaveToWidget(entityType, activeFields)}>
              <i className="fa-solid fa-plus mr-1 sm:mr-2"></i>
              <span className="hidden sm:inline">{t("addToDashboard") || "Add to Dashboard"}</span>
              <span className="sm:hidden">{t("addToDashboard") || "Add to Dashboard"}</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs sm:text-sm">
                <i className="fa-solid fa-sliders mr-1 sm:mr-2"></i>
                <span className="hidden sm:inline">{t("customizeFields") || "Customize Fields"}</span>
                <Badge variant="secondary" className="ml-1 sm:ml-2 h-5">{activeFields.length}/{config.fields.length}</Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 max-h-96 overflow-y-auto">
              <DropdownMenuLabel>{t("selectFields") || "Select Fields"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div className="flex items-center gap-2 px-2 py-1.5">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs flex-1"
                  onClick={selectAllFields}
                >
                  {t("selectAll") || "Select All"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs flex-1"
                  onClick={deselectAllFields}
                >
                  {t("deselectAll") || "Deselect All"}
                </Button>
              </div>
              <DropdownMenuSeparator />
              {config.fields.map((field) => (
                <DropdownMenuCheckboxItem
                  key={field.key}
                  checked={activeFields.includes(field.key)}
                  onCheckedChange={() => toggleField(field.key)}
                  disabled={field.mandatory}
                >
                  <span className={cn(field.mandatory && "font-semibold")}>
                    {t(field.key) || field.label}
                    {field.mandatory && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visibleFields.map((field) => (
          <div key={field.key} className="space-y-1.5">
            <Label 
              htmlFor={`${entityType}-${field.key}`} 
              className={cn("text-sm font-medium flex items-center gap-1", field.mandatory && "font-bold")}
            >
              {t(field.key) || field.label}
              {field.mandatory && <span className="text-red-500 text-sm">*</span>}
            </Label>
            {field.type === "text" && (
              <Input
                id={`${entityType}-${field.key}`}
                placeholder={field.placeholder}
                value={formData[field.key] || ""}
                onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                className={cn("h-9 text-sm", field.mandatory && !formData[field.key] && "border-red-300")}
              />
            )}
            {field.type === "date" && (
              <Input
                id={`${entityType}-${field.key}`}
                type="date"
                value={formData[field.key] || ""}
                onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                className={cn("h-9 text-sm", field.mandatory && !formData[field.key] && "border-red-300")}
              />
            )}
            {field.type === "select" && (
              <Select
                value={formData[field.key] || ""}
                onValueChange={(value) => setFormData({ ...formData, [field.key]: value })}
              >
                <SelectTrigger 
                  id={`${entityType}-${field.key}`} 
                  className={cn("h-9 text-sm", field.mandatory && !formData[field.key] && "border-red-300")}
                >
                  <SelectValue placeholder={`${t("select")}...`} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((opt) => (
                    <SelectItem key={opt} value={opt} className="text-sm">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <Button type="submit" className="h-9 w-full sm:w-auto text-sm">
          <i className="fa-solid fa-magnifying-glass mr-2"></i>
          {t("search")}
        </Button>
      </div>
    </form>
  );
};

// Facet Panel Component
const FacetPanel = ({ results, facets, config, activeFacets, onFacetChange }) => {
  const { t } = useLanguage();
  
  // Calculate facet counts
  const facetCounts = {};
  facets.forEach(facetKey => {
    facetCounts[facetKey] = {};
    results.forEach(item => {
      const value = item[facetKey];
      if (value) {
        facetCounts[facetKey][value] = (facetCounts[facetKey][value] || 0) + 1;
      }
    });
  });

  return (
    <div className="space-y-4">
      {facets.map(facetKey => {
        const counts = facetCounts[facetKey];
        if (Object.keys(counts).length === 0) return null;
        
        return (
          <div key={facetKey} className="space-y-2">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide">
              {t(facetKey) || facetKey}
            </h4>
            <div className="space-y-1">
              {Object.entries(counts).map(([value, count]) => (
                <div key={value} className="flex items-center gap-2">
                  <Checkbox
                    id={`facet-${facetKey}-${value}`}
                    checked={activeFacets[facetKey]?.includes(value) || false}
                    onCheckedChange={(checked) => onFacetChange(facetKey, value, checked)}
                  />
                  <Label 
                    htmlFor={`facet-${facetKey}-${value}`} 
                    className="text-xs cursor-pointer flex-1 capitalize"
                  >
                    {value}
                  </Label>
                  <Badge variant="secondary" className="text-[10px] h-4">{count}</Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Results View Component with filters, facets, and pagination
const ResultsView = ({ entityType, results, searchCriteria, onBack, config, onViewDetail, onAddToGraph }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("table");
  const [sortBy, setSortBy] = useState("relevance");
  const [selectedResults, setSelectedResults] = useState([]);
  const [activeFacets, setActiveFacets] = useState({});
  const [quickFilter, setQuickFilter] = useState("");
  const [showFilters, setShowFilters] = useState(window.innerWidth >= 1024); // Show on desktop by default
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleFacetChange = (facetKey, value, checked) => {
    setActiveFacets(prev => {
      const currentValues = prev[facetKey] || [];
      if (checked) {
        return { ...prev, [facetKey]: [...currentValues, value] };
      } else {
        return { ...prev, [facetKey]: currentValues.filter(v => v !== value) };
      }
    });
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearAllFacets = () => {
    setActiveFacets({});
    setQuickFilter("");
    setCurrentPage(1);
  };

  const toggleSelectResult = (result) => {
    setSelectedResults(prev => {
      if (prev.find(r => r.id === result.id)) {
        return prev.filter(r => r.id !== result.id);
      }
      return [...prev, result];
    });
  };

  const selectAllOnPage = () => {
    const pageResults = paginatedResults;
    setSelectedResults(prev => {
      const newSelected = [...prev];
      pageResults.forEach(r => {
        if (!newSelected.find(s => s.id === r.id)) {
          newSelected.push(r);
        }
      });
      return newSelected;
    });
  };

  const deselectAll = () => {
    setSelectedResults([]);
  };

  const handleAddSelectedToGraph = () => {
    if (selectedResults.length > 0 && onAddToGraph) {
      onAddToGraph(selectedResults, entityType);
    }
  };

  // Apply facets and quick filter
  let filteredResults = [...results];
  
  // Apply quick filter
  if (quickFilter) {
    filteredResults = filteredResults.filter(item =>
      Object.values(item).some(val => 
        String(val).toLowerCase().includes(quickFilter.toLowerCase())
      )
    );
  }
  
  // Apply facets
  Object.entries(activeFacets).forEach(([facetKey, values]) => {
    if (values.length > 0) {
      filteredResults = filteredResults.filter(item => values.includes(item[facetKey]));
    }
  });

  // Sort results
  const sortedResults = [...filteredResults].sort((a, b) => {
    if (sortBy === "relevance") return b.relevance - a.relevance;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = sortedResults.slice(startIndex, startIndex + itemsPerPage);

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-green-500/10 text-green-500 border-green-500/30",
      inactive: "bg-gray-500/10 text-gray-500 border-gray-500/30",
      operational: "bg-green-500/10 text-green-500 border-green-500/30",
      planned: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      closed: "bg-red-500/10 text-red-500 border-red-500/30",
      public: "bg-purple-500/10 text-purple-500 border-purple-500/30",
      private: "bg-orange-500/10 text-orange-500 border-orange-500/30",
      upcoming: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      ongoing: "bg-green-500/10 text-green-500 border-green-500/30",
      completed: "bg-gray-500/10 text-gray-500 border-gray-500/30",
      cancelled: "bg-red-500/10 text-red-500 border-red-500/30",
      draft: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
      pending: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      approved: "bg-green-500/10 text-green-500 border-green-500/30",
      archived: "bg-gray-500/10 text-gray-500 border-gray-500/30",
    };
    return variants[status] || "bg-muted text-muted-foreground";
  };

  const activeFacetCount = Object.values(activeFacets).flat().length;

  return (
    <div className="flex gap-4 h-full">
      {/* Left Facets Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="fixed lg:relative inset-y-0 left-0 lg:inset-auto w-64 lg:w-56 shrink-0 bg-background lg:bg-transparent border-r border-border lg:pr-4 z-50 lg:z-auto shadow-xl lg:shadow-none"
          >
            <div className="flex items-center justify-between mb-4 p-4 lg:p-0 border-b lg:border-b-0 border-border">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <i className="fa-solid fa-filter text-primary text-xs"></i>
                {t("filters")}
              </h3>
              <div className="flex items-center gap-2">
                {activeFacetCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearAllFacets}>
                    {t("clear")}
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-6 w-6 lg:hidden" onClick={() => setShowFilters(false)}>
                  <i className="fa-solid fa-xmark text-xs"></i>
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[calc(100vh-10rem)] lg:h-[calc(100vh-20rem)] px-4 lg:px-0">
              <FacetPanel
                results={results}
                facets={config.facets}
                config={config}
                activeFacets={activeFacets}
                onFacetChange={handleFacetChange}
              />
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Mobile overlay backdrop */}
      {showFilters && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setShowFilters(false)}
        />
      )}

      {/* Main Results Area */}
      <div className="flex-1 space-y-4 min-w-0">
        {/* Results Header */}
        <div className="flex flex-col gap-3 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className={cn("fa-solid", showFilters ? "fa-xmark lg:fa-chevron-left" : "fa-filter")}></i>
              </Button>
              <div>
                <p className="text-xs sm:text-sm">
                  <span className="font-semibold text-foreground">{sortedResults.length}</span>
                  <span className="text-muted-foreground"> / {results.length} {t("results")}</span>
                  {selectedResults.length > 0 && (
                    <span className="ml-2 text-primary text-xs">({selectedResults.length})</span>
                  )}
                </p>
              </div>
            </div>
            {/* View mode toggle - always visible */}
            <div className="flex items-center gap-1 border rounded-md p-0.5">
              <Button 
                variant={viewMode === "table" ? "secondary" : "ghost"} 
                size="icon" 
                className="h-7 w-7"
                onClick={() => setViewMode("table")}
              >
                <i className="fa-solid fa-table-list text-xs"></i>
              </Button>
              <Button 
                variant={viewMode === "cards" ? "secondary" : "ghost"} 
                size="icon" 
                className="h-7 w-7"
                onClick={() => setViewMode("cards")}
              >
                <i className="fa-solid fa-grip text-xs"></i>
              </Button>
            </div>
          </div>
          
          {/* Second row - actions and filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Selection controls */}
            {selectedResults.length > 0 && (
              <div className="flex items-center gap-2 pr-2 border-r border-border">
                <Button 
                  variant="default" 
                  size="sm" 
                  className="h-8 text-xs"
                  onClick={handleAddSelectedToGraph}
                >
                  <i className="fa-solid fa-circle-nodes mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">{t("addToGraph") || "Add to Graph"}</span>
                  <span className="sm:hidden">Add</span>
                  <span className="ml-1">({selectedResults.length})</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 text-xs"
                  onClick={deselectAll}
                >
                  <i className="fa-solid fa-xmark"></i>
                </Button>
              </div>
            )}
            {/* Quick Filter */}
            <div className="relative flex-1 min-w-[120px] max-w-[200px]">
              <i className="fa-solid fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
              <Input
                placeholder={t("filter") || "Filter..."}
                value={quickFilter}
                onChange={(e) => { setQuickFilter(e.target.value); setCurrentPage(1); }}
                className="h-8 pl-8 text-xs sm:text-sm"
              />
            </div>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-24 sm:w-32 h-8 text-xs sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">{t("relevance")}</SelectItem>
                <SelectItem value="name">{t("name")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filters Display */}
        {(Object.keys(searchCriteria).length > 0 || activeFacetCount > 0 || quickFilter) && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(searchCriteria).map(([key, value]) => value && (
              <Badge key={`search-${key}`} variant="secondary" className="text-xs">
                {t(key) || key}: {value}
              </Badge>
            ))}
            {Object.entries(activeFacets).flatMap(([facetKey, values]) =>
              values.map(value => (
                <Badge 
                  key={`facet-${facetKey}-${value}`} 
                  variant="outline" 
                  className="text-xs cursor-pointer hover:bg-destructive/10"
                  onClick={() => handleFacetChange(facetKey, value, false)}
                >
                  {t(facetKey)}: {value}
                  <i className="fa-solid fa-xmark ml-1"></i>
                </Badge>
              ))
            )}
            {quickFilter && (
              <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => setQuickFilter("")}>
                Filter: {quickFilter}
                <i className="fa-solid fa-xmark ml-1"></i>
              </Badge>
            )}
          </div>
        )}

        {/* Results Content */}
        <div 
          className="relative w-full"
          style={{ height: 'calc(100vh - 24rem)' }}
        >
          {viewMode === "table" ? (
            <div 
              className="absolute inset-0 overflow-auto overscroll-contain rounded-lg border border-border"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <Table className="w-full" style={{ minWidth: '800px' }}>
                <TableHeader className="sticky top-0 bg-card z-10 shadow-sm">
                  <TableRow className="h-12">
                    <TableHead className="w-12 px-3">
                      <Checkbox 
                        checked={paginatedResults.length > 0 && paginatedResults.every(r => selectedResults.find(s => s.id === r.id))}
                        onCheckedChange={(checked) => checked ? selectAllOnPage() : deselectAll()}
                      />
                    </TableHead>
                    {config.columns.map((col) => (
                      <TableHead 
                        key={col} 
                        className="text-xs font-medium whitespace-nowrap px-3 cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => { setSortBy(col); setCurrentPage(1); }}
                      >
                        <div className="flex items-center gap-1.5">
                          {t(col) || col.charAt(0).toUpperCase() + col.slice(1)}
                          <i className={cn(
                            "fa-solid text-[10px] text-muted-foreground",
                            sortBy === col ? "fa-sort-down text-primary" : "fa-sort"
                          )}></i>
                        </div>
                      </TableHead>
                    ))}
                    <TableHead 
                      className="text-xs font-medium px-3 cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => { setSortBy("relevance"); setCurrentPage(1); }}
                    >
                      <div className="flex items-center gap-1.5">
                        {t("relevance")}
                        <i className={cn(
                          "fa-solid text-[10px] text-muted-foreground",
                          sortBy === "relevance" ? "fa-sort-down text-primary" : "fa-sort"
                        )}></i>
                      </div>
                    </TableHead>
                    <TableHead className="text-right text-xs font-medium px-3 w-28">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedResults.map((result) => (
                    <TableRow 
                      key={result.id} 
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 h-14",
                        selectedResults.find(r => r.id === result.id) && "bg-primary/5"
                      )}
                    >
                      <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={!!selectedResults.find(r => r.id === result.id)}
                          onCheckedChange={() => toggleSelectResult(result)}
                        />
                      </TableCell>
                      {config.columns.map((col) => (
                        <TableCell key={col} className="text-sm px-3 py-3" onClick={() => onViewDetail(result)}>
                          {col === "status" || col === "classification" ? (
                            <Badge variant="outline" className={cn("capitalize text-xs", getStatusBadge(result[col]))}>
                              {result[col]}
                            </Badge>
                          ) : col === "name" ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="flex h-8 w-8 items-center justify-center rounded-lg shrink-0" 
                                style={{ backgroundColor: `${config.color}20` }}
                              >
                                <i className={cn("fa-solid", config.icon, "text-sm")} style={{ color: config.color }}></i>
                              </div>
                              <span className="font-medium">{result[col]}</span>
                            </div>
                          ) : (
                            <span className="truncate max-w-[150px] block">{result[col] || "-"}</span>
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full" 
                              style={{ 
                                width: `${result.relevance}%`,
                                backgroundColor: result.relevance >= 90 ? "hsl(var(--success))" : result.relevance >= 70 ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"
                              }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium w-8">{result.relevance}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right px-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); onAddToGraph([result], entityType); }}
                            title={t("addToGraph") || "Add to Graph"}
                          >
                            <i className="fa-solid fa-circle-nodes text-sm"></i>
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 text-xs"
                            onClick={(e) => { e.stopPropagation(); onViewDetail(result); }}
                          >
                            <i className="fa-solid fa-eye mr-1"></i>
                            {t("view")}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedResults.map((result, index) => (
                <motion.div
                  key={result.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card 
                    className={cn(
                      "cursor-pointer transition-all hover:border-primary/50 h-full",
                      selectedResults.find(r => r.id === result.id) && "border-primary bg-primary/5"
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center gap-2">
                          <Checkbox 
                            checked={!!selectedResults.find(r => r.id === result.id)}
                            onCheckedChange={() => toggleSelectResult(result)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div 
                            className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0" 
                            style={{ backgroundColor: `${config.color}20` }}
                            onClick={() => onViewDetail(result)}
                          >
                            <i className={cn("fa-solid", config.icon, "text-base")} style={{ color: config.color }}></i>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => onViewDetail(result)}>
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-semibold text-sm truncate">{result.name}</h4>
                            <span className={cn(
                              "text-xs font-semibold",
                              result.relevance >= 90 ? "text-green-500" : result.relevance >= 70 ? "text-primary" : "text-muted-foreground"
                            )}>
                              {result.relevance}%
                            </span>
                          </div>
                          <div className="mt-2 space-y-1">
                            {config.columns.slice(1, 5).map((col) => result[col] && (
                              <p key={col} className="text-xs text-muted-foreground flex items-center gap-1">
                                <span className="text-muted-foreground/70 capitalize">{t(col) || col}:</span>
                                {col === "status" ? (
                                  <Badge variant="outline" className={cn("capitalize text-[10px] h-4", getStatusBadge(result[col]))}>
                                    {result[col]}
                                  </Badge>
                                ) : (
                                  <span className="truncate">{result[col]}</span>
                                )}
                              </p>
                            ))}
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs flex-1"
                              onClick={(e) => { e.stopPropagation(); onAddToGraph([result], entityType); }}
                            >
                              <i className="fa-solid fa-circle-nodes mr-1"></i>
                              {t("graph")}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs flex-1"
                              onClick={(e) => { e.stopPropagation(); onViewDetail(result); }}
                            >
                              <i className="fa-solid fa-eye mr-1"></i>
                              {t("view")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {sortedResults.length === 0 && (
            <Card className="p-12">
              <div className="text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mx-auto mb-4">
                  <i className="fa-solid fa-magnifying-glass text-2xl text-muted-foreground"></i>
                </div>
                <h3 className="font-semibold text-lg">{t("noResults") || "No Results Found"}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("tryDifferentCriteria") || "Try adjusting your filters"}
                </p>
                <Button variant="outline" className="mt-4" onClick={clearAllFacets}>
                  <i className="fa-solid fa-rotate-left mr-2"></i>
                  {t("clearFilters")}
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-border gap-3">
            <p className="text-xs text-muted-foreground">
              {t("showing") || "Showing"} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedResults.length)} {t("of")} {sortedResults.length}
            </p>
            <div className="flex items-center gap-1">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <i className="fa-solid fa-angles-left text-xs"></i>
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <i className="fa-solid fa-chevron-left text-xs"></i>
              </Button>
              
              {/* Show page numbers on larger screens, just current on mobile */}
              <span className="sm:hidden text-xs text-muted-foreground px-2">
                {currentPage} / {totalPages}
              </span>
              <div className="hidden sm:flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8 text-xs"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              </div>
              
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <i className="fa-solid fa-chevron-right text-xs"></i>
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <i className="fa-solid fa-angles-right text-xs"></i>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Detail View Component with Gallery, Edit functionality, and Paginator
const DetailView = ({ item, entityType, config, onBack, onAddToGraph, onUpdateItem, allResults, onNavigate }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedItem, setEditedItem] = useState({ ...item });
  const images = mockImages[entityType] || [];

  // Find current index in results for pagination
  const currentIndex = allResults?.findIndex(r => r.id === item.id) ?? -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allResults?.length || 0) - 1;

  const handlePrevious = () => {
    if (hasPrevious && onNavigate) {
      onNavigate(allResults[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(allResults[currentIndex + 1]);
    }
  };

  const handleSave = () => {
    if (onUpdateItem) {
      onUpdateItem(editedItem);
    }
    setEditMode(false);
  };

  const handleCancel = () => {
    setEditedItem({ ...item });
    setEditMode(false);
  };

  const updateField = (key, value) => {
    setEditedItem(prev => ({ ...prev, [key]: value }));
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-green-500/10 text-green-500 border-green-500/30",
      inactive: "bg-gray-500/10 text-gray-500 border-gray-500/30",
      operational: "bg-green-500/10 text-green-500 border-green-500/30",
      public: "bg-purple-500/10 text-purple-500 border-purple-500/30",
      private: "bg-orange-500/10 text-orange-500 border-orange-500/30",
      upcoming: "bg-blue-500/10 text-blue-500 border-blue-500/30",
      completed: "bg-gray-500/10 text-gray-500 border-gray-500/30",
      approved: "bg-green-500/10 text-green-500 border-green-500/30",
      draft: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
      pending: "bg-blue-500/10 text-blue-500 border-blue-500/30",
    };
    return variants[status] || "";
  };

  // Group attributes by category
  const basicFields = ["name", "status", "type"];
  const contactFields = ["email", "phone", "mobile", "website", "linkedIn", "address"];
  const orgFields = ["company", "department", "role", "organizer", "author"];
  const locationFields = ["location", "city", "state", "country", "region", "headquarters", "venue"];
  const dateFields = ["created", "modified", "date", "endDate", "founded", "dateOfBirth", "lastLogin", "lastAudit"];
  const financialFields = ["revenue", "salary", "budget", "ticketPrice", "marketCap", "monthlyRent"];
  
  const displayItem = editMode ? editedItem : item;
  
  const categorizedFields = {
    basic: Object.entries(displayItem).filter(([k]) => basicFields.includes(k)),
    contact: Object.entries(displayItem).filter(([k]) => contactFields.includes(k)),
    organization: Object.entries(displayItem).filter(([k]) => orgFields.includes(k)),
    location: Object.entries(displayItem).filter(([k]) => locationFields.includes(k)),
    dates: Object.entries(displayItem).filter(([k]) => dateFields.includes(k)),
    financial: Object.entries(displayItem).filter(([k]) => financialFields.includes(k)),
    other: Object.entries(displayItem).filter(([k]) => 
      !["id", "relevance", ...basicFields, ...contactFields, ...orgFields, ...locationFields, ...dateFields, ...financialFields].includes(k)
    ),
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div 
              className="flex h-20 w-20 items-center justify-center rounded-2xl shrink-0" 
              style={{ backgroundColor: `${config.color}20` }}
            >
              <i className={cn("fa-solid", config.icon, "text-3xl")} style={{ color: config.color }}></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold">{item.name}</h1>
                {displayItem.status && (
                  <Badge variant="outline" className={cn("capitalize", getStatusBadge(displayItem.status))}>
                    {displayItem.status}
                  </Badge>
                )}
                {editMode && (
                  <Badge variant="default" className="bg-primary">
                    <i className="fa-solid fa-pen mr-1 text-[10px]"></i>
                    {t("editing") || "Editing"}
                  </Badge>
                )}
              </div>
              {displayItem.role && <p className="text-muted-foreground mt-1">{displayItem.role} {displayItem.company && `at ${displayItem.company}`}</p>}
              {displayItem.industry && <p className="text-muted-foreground mt-1">{displayItem.industry} • {displayItem.headquarters}</p>}
              {displayItem.type && entityType !== "person" && <p className="text-muted-foreground mt-1">{displayItem.type}</p>}
              
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {!editMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                      <i className="fa-solid fa-pen mr-2"></i>{t("edit")}
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => onAddToGraph([item], entityType)}
                    >
                      <i className="fa-solid fa-circle-nodes mr-2"></i>{t("addToGraph") || "Add to Graph"}
                    </Button>
                    <Button variant="outline" size="sm">
                      <i className="fa-solid fa-download mr-2"></i>{t("export")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="default" size="sm" onClick={handleSave}>
                      <i className="fa-solid fa-check mr-2"></i>{t("save")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleCancel}>
                      <i className="fa-solid fa-xmark mr-2"></i>{t("cancel")}
                    </Button>
                  </>
                )}
                
                {/* Paginator - Next/Previous Result */}
                {allResults && allResults.length > 1 && (
                  <div className="flex items-center gap-1 ml-auto border-l pl-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handlePrevious}
                      disabled={!hasPrevious}
                      className="h-8 w-8 p-0"
                    >
                      <i className="fa-solid fa-chevron-left text-xs"></i>
                    </Button>
                    <span className="text-xs text-muted-foreground px-2 min-w-[60px] text-center">
                      {currentIndex + 1} / {allResults.length}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleNext}
                      disabled={!hasNext}
                      className="h-8 w-8 p-0"
                    >
                      <i className="fa-solid fa-chevron-right text-xs"></i>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t("relevance")}</p>
              <p className={cn(
                "text-3xl font-bold",
                displayItem.relevance >= 90 ? "text-green-500" : displayItem.relevance >= 70 ? "text-primary" : "text-muted-foreground"
              )}>
                {displayItem.relevance}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - All Attributes */}
        <div className="lg:col-span-2 space-y-4">
          {/* Contact Information */}
          {categorizedFields.contact.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-address-book text-primary"></i>
                  {t("contactInformation") || "Contact Information"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorizedFields.contact.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key}</p>
                      {editMode ? (
                        <Input 
                          value={editedItem[key] || ""} 
                          onChange={(e) => updateField(key, e.target.value)}
                          className="h-8 text-sm"
                        />
                      ) : (
                        <p className="text-sm font-medium">{value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Organization Info */}
          {categorizedFields.organization.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-building text-primary"></i>
                  {t("organizationInfo") || "Organization Info"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorizedFields.organization.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key}</p>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Location Info */}
          {categorizedFields.location.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-primary"></i>
                  {t("locationInfo") || "Location Info"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorizedFields.location.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key}</p>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Financial Info */}
          {categorizedFields.financial.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-dollar-sign text-primary"></i>
                  {t("financialInfo") || "Financial Info"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorizedFields.financial.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key}</p>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          {categorizedFields.dates.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-calendar text-primary"></i>
                  {t("dates") || "Dates"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorizedFields.dates.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key}</p>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other Attributes */}
          {categorizedFields.other.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-primary"></i>
                  {t("additionalInfo") || "Additional Information"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categorizedFields.other.map(([key, value]) => value && value !== "-" && (
                    <div key={key} className="space-y-1">
                      <p className="text-xs text-muted-foreground capitalize">{t(key) || key.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className="text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Sidebar - Gallery */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <i className="fa-solid fa-images text-primary"></i>
                {t("gallery") || "Gallery"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {images.map((img, idx) => (
                  <div 
                    key={idx} 
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                    onClick={() => setSelectedImage(img)}
                  >
                    <img 
                      src={img} 
                      alt={`${item.name} - ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {images.length} {t("images") || "images"}
              </p>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <i className="fa-solid fa-bolt text-primary"></i>
                {t("quickActions") || "Quick Actions"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start h-9 text-sm">
                <i className="fa-solid fa-share-nodes mr-2"></i>
                {t("share") || "Share"}
              </Button>
              <Button variant="outline" className="w-full justify-start h-9 text-sm">
                <i className="fa-solid fa-print mr-2"></i>
                {t("print") || "Print"}
              </Button>
              <Button variant="outline" className="w-full justify-start h-9 text-sm">
                <i className="fa-solid fa-file-pdf mr-2"></i>
                {t("exportPDF") || "Export PDF"}
              </Button>
              <Button variant="outline" className="w-full justify-start h-9 text-sm text-destructive hover:text-destructive">
                <i className="fa-solid fa-trash mr-2"></i>
                {t("delete")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Image Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{item.name}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="relative">
              <img 
                src={selectedImage} 
                alt={item.name}
                className="w-full h-auto rounded-lg"
              />
              <div className="flex justify-center gap-2 mt-4">
                {images.map((img, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      "w-16 h-16 rounded-lg overflow-hidden cursor-pointer transition-all",
                      selectedImage === img ? "ring-2 ring-primary" : "opacity-60 hover:opacity-100"
                    )}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function SearchResults() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("person");
  const [view, setView] = useState("search"); // "search", "results", "detail"
  const [searchResults, setSearchResults] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [searchedEntityType, setSearchedEntityType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  // Handle search
  const handleSearch = (entityType, criteria) => {
    let results = [...mockDatabase[entityType]];
    
    Object.entries(criteria).forEach(([key, value]) => {
      if (value) {
        results = results.filter(item => {
          const itemValue = String(item[key] || "").toLowerCase();
          return itemValue.includes(String(value).toLowerCase());
        });
      }
    });

    setSearchResults(results);
    setSearchCriteria(criteria);
    setSearchedEntityType(entityType);
    setView("results");
    setSearchParams({ type: entityType, view: "results" });
  };

  // Handle back
  const handleBackToSearch = () => {
    setView("search");
    setSearchResults([]);
    setSearchCriteria({});
    setSelectedItem(null);
    setSearchParams({});
  };

  const handleBackToResults = () => {
    setView("results");
    setSelectedItem(null);
  };

  // Handle view detail
  const handleViewDetail = (item) => {
    setSelectedItem(item);
    setView("detail");
    setSearchParams({ type: searchedEntityType, view: "detail", id: item.id });
  };

  // Handle update item from detail edit mode
  const handleUpdateItem = (updatedItem) => {
    // Update in search results
    setSearchResults(prev => prev.map(r => r.id === updatedItem.id ? updatedItem : r));
    // Update selected item
    setSelectedItem(updatedItem);
  };

  // Handle Add to Graph - navigate to Graph Explorer with selected items
  const handleAddToGraph = (items, entityType) => {
    // Store selected items in sessionStorage for Graph Explorer to pick up
    const graphData = items.map(item => ({
      id: `search-${entityType}-${item.id}`,
      label: item.name,
      group: entityType,
      properties: item
    }));
    sessionStorage.setItem('graphImportData', JSON.stringify(graphData));
    // Navigate to Graph Explorer using React Router
    navigate('/graph?import=true');
  };

  // Handle Save to Dashboard Widget
  const handleSaveToWidget = (entityType, fields) => {
    // Store widget config in sessionStorage for Dashboard to pick up
    const widgetConfig = {
      type: 'search',
      entityType,
      fields,
      title: `${entityType} Search`
    };
    const existingWidgets = JSON.parse(sessionStorage.getItem('dashboardWidgets') || '[]');
    existingWidgets.push(widgetConfig);
    sessionStorage.setItem('dashboardWidgets', JSON.stringify(existingWidgets));
    toast.success(t("widgetAdded") || "Widget added to Dashboard!", {
      description: t("goToDashboardToView") || "Go to Dashboard to view your saved search form"
    });
  };

  // Initialize from URL params
  useEffect(() => {
    const type = searchParams.get("type");
    const viewParam = searchParams.get("view");
    if (type && entityConfig[type]) {
      setActiveTab(type);
      setSearchedEntityType(type);
    }
    if (viewParam === "results") {
      setView("results");
    } else if (viewParam === "detail") {
      setView("detail");
    }
  }, [searchParams]);

  const entityTypes = Object.keys(entityConfig);

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Breadcrumbs */}
      <div className="px-4 lg:px-6 py-3 border-b border-border bg-card/50">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              {view === "search" ? (
                <BreadcrumbPage className="text-xs flex items-center gap-1.5">
                  <i className="fa-solid fa-magnifying-glass text-[10px] text-primary"></i>
                  {t("search")}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); handleBackToSearch(); }}
                  className="text-xs flex items-center gap-1.5"
                >
                  <i className="fa-solid fa-magnifying-glass text-[10px]"></i>
                  {t("search")}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {(view === "results" || view === "detail") && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {view === "results" ? (
                    <BreadcrumbPage className="text-xs flex items-center gap-1.5">
                      <i className={cn("fa-solid", entityConfig[searchedEntityType]?.icon, "text-sm sm:text-[10px]")} 
                         style={{ color: entityConfig[searchedEntityType]?.color }}></i>
                      {t(searchedEntityType)} {t("results")}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); handleBackToResults(); }}
                      className="text-xs flex items-center gap-1.5"
                    >
                      <i className={cn("fa-solid", entityConfig[searchedEntityType]?.icon, "text-sm sm:text-[10px]")} 
                         style={{ color: entityConfig[searchedEntityType]?.color }}></i>
                      {t(searchedEntityType)} {t("results")}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </>
            )}
            {view === "detail" && selectedItem && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-xs">
                    {selectedItem.name}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Main Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 lg:p-6">
          <AnimatePresence mode="wait">
            {view === "search" && (
              <motion.div
                key="search"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Page Header */}
                <div className="mb-6">
                  <h1 className="text-xl lg:text-2xl font-bold text-foreground flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <i className="fa-solid fa-magnifying-glass text-lg text-primary"></i>
                    </div>
                    {t("advancedSearch") || "Advanced Search"}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1 ml-13">
                    {t("searchDescription") || "Search across all entity types with specific attributes"}
                  </p>
                </div>

                {/* Entity Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="flex flex-wrap h-auto gap-1.5 sm:gap-1 bg-muted/50 p-1.5 sm:p-1 mb-6 overflow-x-auto">
                    {entityTypes.map((type) => {
                      const config = entityConfig[type];
                      return (
                        <TabsTrigger 
                          key={type} 
                          value={type}
                          className="flex items-center gap-1.5 sm:gap-2 data-[state=active]:bg-background h-11 sm:h-9 px-3 sm:px-4 text-xs whitespace-nowrap"
                        >
                          <i className={cn("fa-solid", config.icon, "text-sm sm:text-xs")} style={{ color: config.color }}></i>
                          <span className="capitalize hidden sm:inline">{t(type) || type}</span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {entityTypes.map((type) => {
                    const config = entityConfig[type];
                    return (
                      <TabsContent key={type} value={type}>
                        <Card>
                          <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-3 text-lg">
                              <div 
                                className="flex h-9 w-9 items-center justify-center rounded-lg" 
                                style={{ backgroundColor: `${config.color}20` }}
                              >
                                <i className={cn("fa-solid", config.icon)} style={{ color: config.color }}></i>
                              </div>
                              {t("searchFor")} {t(type) || type}
                            </CardTitle>
                            <CardDescription>
                              {t("enterCriteria") || "Enter search criteria to find"} {t(type) || type}s
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <SearchForm 
                              entityType={type} 
                              onSearch={handleSearch} 
                              config={config}
                              onSaveToWidget={handleSaveToWidget}
                            />
                          </CardContent>
                        </Card>
                      </TabsContent>
                    );
                  })}
                </Tabs>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-6">
                  {entityTypes.map((type) => {
                    const config = entityConfig[type];
                    const count = mockDatabase[type].length;
                    return (
                      <Card 
                        key={type} 
                        className="cursor-pointer hover:border-primary/50 transition-all"
                        onClick={() => setActiveTab(type)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="flex h-10 w-10 items-center justify-center rounded-lg shrink-0" 
                              style={{ backgroundColor: `${config.color}15` }}
                            >
                              <i className={cn("fa-solid", config.icon)} style={{ color: config.color }}></i>
                            </div>
                            <div>
                              <p className="text-2xl font-bold">{count}</p>
                              <p className="text-xs text-muted-foreground capitalize">{t(type) || type}s</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {view === "results" && (
              <motion.div
                key="results"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Results Header */}
                <div className="flex items-center gap-4 mb-6">
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleBackToSearch}>
                    <i className="fa-solid fa-arrow-left"></i>
                  </Button>
                  <div>
                    <h1 className="text-xl lg:text-2xl font-bold text-foreground flex items-center gap-3">
                      <div 
                        className="flex h-12 w-12 sm:h-10 sm:w-10 items-center justify-center rounded-xl" 
                        style={{ backgroundColor: `${entityConfig[searchedEntityType]?.color}20` }}
                      >
                        <i className={cn("fa-solid", entityConfig[searchedEntityType]?.icon, "text-xl sm:text-lg")} 
                           style={{ color: entityConfig[searchedEntityType]?.color }}></i>
                      </div>
                      {t(searchedEntityType) || searchedEntityType} {t("results")}
                    </h1>
                  </div>
                </div>

                <ResultsView 
                  entityType={searchedEntityType}
                  results={searchResults}
                  searchCriteria={searchCriteria}
                  onBack={handleBackToSearch}
                  config={entityConfig[searchedEntityType]}
                  onViewDetail={handleViewDetail}
                  onAddToGraph={handleAddToGraph}
                />
              </motion.div>
            )}

            {view === "detail" && selectedItem && (
              <motion.div
                key="detail"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Detail Header */}
                <div className="flex items-center gap-4 mb-6">
                  <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleBackToResults}>
                    <i className="fa-solid fa-arrow-left"></i>
                  </Button>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t(searchedEntityType)} {t("details") || "Details"}
                    </p>
                  </div>
                </div>

                <DetailView 
                  item={selectedItem}
                  entityType={searchedEntityType}
                  config={entityConfig[searchedEntityType]}
                  onBack={handleBackToResults}
                  onAddToGraph={handleAddToGraph}
                  onUpdateItem={handleUpdateItem}
                  allResults={searchResults}
                  onNavigate={handleViewDetail}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ScrollArea>
    </div>
  );
}
